/*
  Paired-metafield range slider for the collection rail.

  WHY THIS EXISTS AND WHY IT IS NOT A price_range FILTER
  -----------------------------------------------------
  Shopify renders a metafield filter as type `list` — discrete values only.
  `.gte` / `.lte` are NOT supported on metafields; they are supported only on
  `filter.v.price`. Passing them anyway does not error, it is silently ignored,
  and an ignored filter returns THE WHOLE COLLECTION. Verified 1 Sep 2026: all
  seven `.gte` probes on Line & Leader returned 28 of 28 products.

  So the slider does not send a range at all. It sends every discrete value
  inside the range, and leans on two documented behaviours:

      repeated values on ONE key  -> OR
      two different filter keys   -> AND

  With a min-metafield and a max-metafield per product that gives true OVERLAP:

      max_lb IN {values >= lo}   AND   min_lb IN {values <= hi}

  i.e. "the product's range overlaps the slider's range", which is what a
  shopper means. Storing only a max would hide a 12-130 lb leader from a
  20-40 lb search. Verified against the live catalogue: overlap 20-40 returned
  20 products, overlap 200-530 returned 3 — both exactly matching the counts
  computed from the source data.

  VALUE FORMAT IS LOAD-BEARING. Shopify matches the stored string, not the
  number: `=130.0` returns 2 products, `=130` returns ZERO. The raw value
  strings are therefore passed through from Liquid untouched and never
  re-formatted here. Do not "tidy" them.
*/
(function () {
  'use strict';

  var CLEARED = null; // sentinel: slider at full extent means "no filter"

  function parseValues(el, attr) {
    try {
      return JSON.parse(el.getAttribute(attr) || '[]');
    } catch (e) {
      return [];
    }
  }

  function fmt(n) {
    // display only — never used to build a query value
    return (Math.round(n * 10) / 10).toString().replace(/\.0$/, '');
  }

  function init(root) {
    if (root.dataset.rangeReady === '1') return;
    root.dataset.rangeReady = '1';

    var minValues = parseValues(root, 'data-min-values'); // strings, exact
    var maxValues = parseValues(root, 'data-max-values');
    var minParam = root.getAttribute('data-min-param');
    var maxParam = root.getAttribute('data-max-param');
    var baseUrl = root.getAttribute('data-base-url') || window.location.pathname;

    var lo = root.querySelector('[data-range-lo]');
    var hi = root.querySelector('[data-range-hi]');
    var out = root.querySelector('[data-range-out]');
    var apply = root.querySelector('[data-range-apply]');
    var clear = root.querySelector('[data-range-clear]');
    if (!lo || !hi) return;

    var domainMin = parseFloat(root.getAttribute('data-domain-min'));
    var domainMax = parseFloat(root.getAttribute('data-domain-max'));

    // Restore the exact positions the visitor chose, if we carried them.
    // Liquid seeds the handles from the selected values, which snaps to the
    // nearest data point; these put them back where they were actually left.
    var q = new URLSearchParams(window.location.search);
    var srLo = parseFloat(q.get('sr_lo'));
    var srHi = parseFloat(q.get('sr_hi'));
    if (!isNaN(srLo) && srLo >= domainMin && srLo <= domainMax) lo.value = srLo;
    if (!isNaN(srHi) && srHi >= domainMin && srHi <= domainMax) hi.value = srHi;

    /* Unit comes off the block (from the filter label). Empty for PE, which is
       a bare number — a hardcoded 'lb' was showing on every slider. */
    var unitSuffix = root.dataset.unit ? ' ' + root.dataset.unit : '';

    function readout() {
      var a = Math.min(+lo.value, +hi.value);
      var b = Math.max(+lo.value, +hi.value);
      if (out) out.textContent = fmt(a) + ' – ' + fmt(b) + unitSuffix;
      // keep the visual fill in sync
      var span = domainMax - domainMin || 1;
      root.style.setProperty('--range-a', ((a - domainMin) / span) * 100 + '%');
      root.style.setProperty('--range-b', ((b - domainMin) / span) * 100 + '%');
    }

    // Handles must not cross; clamp rather than swap so the thumb under the
    // cursor keeps following it.
    lo.addEventListener('input', function () {
      if (+lo.value > +hi.value) lo.value = hi.value;
      readout();
    });
    hi.addEventListener('input', function () {
      if (+hi.value < +lo.value) hi.value = lo.value;
      readout();
    });

    function buildUrl(a, b) {
      // Preserve every other active facet: start from the current query, drop
      // this pair's keys and the pager, then re-add.
      var params = new URLSearchParams(window.location.search);
      params.delete(minParam);
      params.delete(maxParam);
      params.delete('page');
      params.delete('sr_lo');
      params.delete('sr_hi');

      if (a !== CLEARED) {
        /*
          Carry the literal handle positions so they can be restored exactly.
          Without these the handles are re-derived from the SELECTED VALUES on
          reload, which snaps them to the nearest real data point: ask for
          20-40 and it comes back 20-35, because no product's minimum is 40.
          The result set is identical either way, but the control looks like it
          discarded the input. Shopify ignores unknown params, so these are
          display state only and never touch filtering.
        */
        params.set('sr_lo', String(a));
        params.set('sr_hi', String(b));
        // AND across the two keys, OR within each -> overlap
        maxValues.forEach(function (v) {
          if (parseFloat(v) >= a) params.append(maxParam, v);
        });
        minValues.forEach(function (v) {
          if (parseFloat(v) <= b) params.append(minParam, v);
        });
      }
      var qs = params.toString();
      return baseUrl + (qs ? '?' + qs : '');
    }

    if (apply) {
      apply.addEventListener('click', function () {
        var a = Math.min(+lo.value, +hi.value);
        var b = Math.max(+lo.value, +hi.value);
        // Full extent means no constraint — send a clean URL rather than
        // ~46 redundant params.
        var full = a <= domainMin && b >= domainMax;
        window.location.href = buildUrl(full ? CLEARED : a, b);
      });
    }

    if (clear) {
      clear.addEventListener('click', function (e) {
        e.preventDefault();
        window.location.href = buildUrl(CLEARED, null);
      });
    }

    readout();
  }

  /*
    The chip is rendered server-side from the SELECTED values, which snap to
    the nearest real data point — ask for 20-40 and Liquid can only say 20-35,
    because no product's minimum is 40. Liquid cannot read query params, so the
    true edges are restored here from sr_lo/sr_hi. Purely cosmetic: the chip's
    clear href is built in Liquid and is not touched.
  */
  function relabelChip() {
    var chip = document.querySelector('[data-range-chip-label]');
    if (!chip) return;
    /* `unitSuffix` in init() is scoped to one block and this runs beside it,
       so read the unit off the page instead — referencing init's copy here
       throws and takes the whole slider down with it. */
    var host = document.querySelector('[data-range-facet]');
    var unitSuffix = host && host.dataset.unit ? ' ' + host.dataset.unit : '';
    var q = new URLSearchParams(window.location.search);
    var a = q.get('sr_lo');
    var b = q.get('sr_hi');
    if (a === null || b === null) return;
    var na = parseFloat(a);
    var nb = parseFloat(b);
    if (isNaN(na) || isNaN(nb)) return;
    chip.textContent = fmt(na) + ' – ' + fmt(nb) + unitSuffix;
  }

  function boot() {
    document.querySelectorAll('[data-range-facet]').forEach(init);
    relabelChip();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // the rail is re-rendered when a facet is applied
  document.addEventListener('shopify:section:load', boot);
})();
